import { getTippingOrigin } from '@utils/helpers';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSetupPreview } from '../SetupPreviewContext';

// Desktop-only live preview of the customer tipping page. Embeds the real
// TA-Tipping app once (preview + iframe mode) and pushes the operator's unsaved
// draft over postMessage so edits reflect instantly with no reload/spinner.

const NAMESPACE = 'tipdirect-preview';
const CHILD_NAMESPACE = 'tipdirect-preview-child';
const DEBOUNCE_MS = 200;

// Which flow screen each Setup section maps to.
export const SECTION_SCREEN = {
  Branding: 'tip',
  // Tours are what the customer rates, so editing them previews the rating
  // screen. Its tour dropdown only appears with 2+ tours; with one tour the
  // same screen shows just the 5-star rating — matching the real flow.
  Tours: 'review',
  Commission: 'tip',
  Upsell: 'upsell',
  Social: 'social',
  // The thank-you message is the greeting on the post-tip chooser (link tree), so
  // focusing it shows that view — same screen the custom links preview on.
  LandingMessage: 'postTip',
  CustomLink: 'postTip'
};

// The customer journey, in order, as the stepper dots below the preview. Every
// screen in SECTION_SCREEN maps to one of these, so the active section always
// lights exactly one dot.
export const FLOW_SCREENS = [
  { screen: 'tip', label: 'Tipping page' },
  { screen: 'postTip', label: 'Thank you & links' },
  { screen: 'review', label: 'Tour rating' },
  { screen: 'upsell', label: 'Upsell' },
  { screen: 'social', label: 'Social' }
];

export default function LivePreviewPanel({ userId, locale }) {
  const { draft, activeSection, setIframeReady } = useSetupPreview();
  const iframeRef = useRef(null);
  const readyRef = useRef(false);
  const debounceRef = useRef(null);
  const latestDraftRef = useRef(draft);

  const origin = getTippingOrigin();
  // src is memoised on userId+origin+locale only so the iframe never re-mounts
  // (edits stream via postMessage, not by changing the URL).
  const src = useMemo(
    () =>
      `${origin}/t/g/${userId}?preview=true&iframe=true&action=tip&locale=${locale || 'en'}`,
    [origin, userId, locale]
  );

  const post = useCallback(
    message => {
      const frame = iframeRef.current;
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ source: NAMESPACE, v: 1, ...message }, origin);
      }
    },
    [origin]
  );

  const flushDraft = useCallback(() => {
    if (readyRef.current) {
      post({ type: 'draft', payload: latestDraftRef.current });
    }
  }, [post]);

  // Listen for the child's handshake + payment-complete.
  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== origin || !event.data || event.data.source !== CHILD_NAMESPACE) {
        return;
      }
      if (event.data.type === 'ready') {
        readyRef.current = true;
        setIframeReady(true);
        flushDraft();
        if (activeSection) {
          post({ type: 'navigate', payload: { screen: SECTION_SCREEN[activeSection] || 'tip' } });
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [origin, flushDraft, post, activeSection, setIframeReady]);

  // Debounced draft push whenever the accumulated draft changes.
  useEffect(() => {
    latestDraftRef.current = draft;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushDraft, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [draft, flushDraft]);

  // Navigate the preview to the focused section's screen.
  useEffect(() => {
    if (readyRef.current && activeSection) {
      post({ type: 'navigate', payload: { screen: SECTION_SCREEN[activeSection] || 'tip' } });
    }
  }, [activeSection, post]);

  // If the child mounted before we attached the listener, ping on load.
  const handleLoad = () => post({ type: 'ping' });

  const currentScreen = SECTION_SCREEN[activeSection] || 'tip';

  return (
    <div className="hidden xl:block sticky top-6 w-[440px] shrink-0 self-start">
      <div className="text-sm font-bold text-slate-800 mb-3">Live preview</div>
      <div className="rounded-3xl border border-solid border-slate-200 shadow-sm overflow-hidden bg-white">
        <iframe
          ref={iframeRef}
          title="Tipping page live preview"
          src={src}
          onLoad={handleLoad}
          className="w-full block border-0"
          style={{ height: '820px' }}
        />
      </div>
      <div
        className="flex items-center justify-center gap-2.5 mt-4"
        role="list"
        aria-label="Preview position in the tipping flow">
        {FLOW_SCREENS.map(({ screen, label }) => {
          const active = screen === currentScreen;
          return (
            <span
              key={screen}
              role="listitem"
              data-testid="flow-step"
              data-active={active}
              title={label}
              aria-label={label}
              aria-current={active ? 'step' : undefined}
              className={`h-2.5 w-2.5 rounded-full border border-solid transition-colors ${
                active ? 'bg-indigo-600 border-indigo-600' : 'bg-transparent border-slate-300'
              }`}
            />
          );
        })}
      </div>
      <div className="text-xs text-slate-400 mt-2 text-center">
        Preview updates as you edit. Nothing is charged.
      </div>
    </div>
  );
}
